import React, { useState } from 'react';
import { LogOut, Library, User, Sparkles, ShoppingBag, ChevronRight, Brain } from './Icons';
import { BrandLogo } from './BrandLogo';
import { ViewState, User as UserType, Child } from '../types';
import { FREE_STORY_LIMIT, PRO_STORY_LIMIT } from '../services/authService';
import ChildSwitcher from './ChildSwitcher';

interface HeaderProps {
  currentView: ViewState;
  onNavigate: (view: ViewState) => void;
  user: UserType | null;
  onLogout: () => void;
  onOpenPricing?: () => void;
  childName?: string;
  appChildren?: Child[];
  activeChild?: Child | null;
  onSelectChild?: (child: Child) => void;
}

const Header: React.FC<HeaderProps> = ({ currentView, onNavigate, user, onLogout, onOpenPricing, childName, appChildren, activeChild, onSelectChild }) => {
  const [showDropdown, setShowDropdown] = useState(false);
  
  const limit = user ? (user.isPro ? PRO_STORY_LIMIT : FREE_STORY_LIMIT) : 0;
  const count = user ? user.dailyStoryCount : 0;
  const remaining = Math.max(0, limit - count);

  const handleSubscriptionClick = () => {
    setShowDropdown(false);
    if (user?.isPro) {
        onNavigate('subscription-manage');
    } else {
        onOpenPricing?.();
    }
  };

  const DropdownMenu = () => (
    <>
      <div className="fixed inset-0 z-40" onClick={() => setShowDropdown(false)}></div>
      <div className="absolute right-0 mt-3 w-64 bg-[#1a1825] border border-white/10 rounded-2xl shadow-2xl p-2 z-50 animate-slide-up">
          <button
              onClick={() => { onNavigate('profile-setup'); setShowDropdown(false); }}
              className="w-full flex items-center justify-between p-3 hover:bg-white/5 rounded-xl transition-colors text-gray-300 hover:text-white text-sm font-medium"
          >
              <div className="flex items-center gap-3">
                  <User className="w-4 h-4 text-gold-400" />
                  Child Profile
              </div>
              <ChevronRight className="w-4 h-4 text-gray-600" />
          </button>

          <button
              onClick={() => { onNavigate('parent-dashboard'); setShowDropdown(false); }}
              className="w-full flex items-center justify-between p-3 hover:bg-white/5 rounded-xl transition-colors text-gray-300 hover:text-white text-sm font-medium"
          >
              <div className="flex items-center gap-3">
                  <Brain className="w-4 h-4 text-forge-400" />
                  Parent Dashboard
              </div>
              <ChevronRight className="w-4 h-4 text-gray-600" />
          </button>

          <button 
              onClick={handleSubscriptionClick}
              className="w-full flex items-center justify-between p-3 hover:bg-white/5 rounded-xl transition-colors text-gray-300 hover:text-white text-sm font-medium"
          >
              <div className="flex items-center gap-3">
                  <ShoppingBag className="w-4 h-4 text-spark-400" />
                  {user?.isPro ? 'My Subscription' : 'Upgrade to Pro'}
              </div>
              <ChevronRight className="w-4 h-4 text-gray-600" />
          </button>

          <div className="h-[1px] bg-white/5 my-2 mx-2"></div>

          <button 
              onClick={() => { onLogout(); setShowDropdown(false); }}
              className="w-full flex items-center gap-3 p-3 hover:bg-red-500/10 rounded-xl transition-colors text-red-400 text-sm font-medium"
          >
              <LogOut className="w-4 h-4" />
              Log Out
          </button>
      </div>
    </>
  );

  return (
    <header className="sticky top-0 w-full py-3 md:py-4 px-4 md:px-12 flex justify-between items-center z-[100] bg-[#1a1825]/60 backdrop-blur-xl border-b border-white/5 no-print shadow-xl">
      <div 
        className="flex items-center gap-3 md:gap-4 cursor-pointer group" 
        onClick={() => onNavigate('landing')}
      >
        <div className="h-10 w-10 md:h-12 md:w-12 transition-all duration-500 group-hover:scale-110 group-hover:drop-shadow-[0_0_15px_rgba(222,160,30,0.4)]">
          <BrandLogo className="w-full h-full" />
        </div>
        
        <div className="flex flex-col justify-center">
            <h1 className="text-xl md:text-2xl font-bold font-sans tracking-tight text-white leading-tight flex items-center gap-1">
                Story<span className="text-gold-400">Forge</span>
            </h1>
            <div className="flex items-center gap-1.5 opacity-60">
                <div className="h-[1px] w-4 bg-gold-500"></div>
                <span className="text-[10px] md:text-xs uppercase tracking-[0.4em] text-white font-sans font-bold">
                    Kids
                </span>
            </div>
        </div>
      </div>

      <nav className="hidden md:flex gap-10 items-center font-semibold text-sm tracking-wide">
        <button 
            onClick={() => onNavigate('landing')} 
            className={`transition-all duration-300 ${currentView === 'landing' ? 'text-gold-400' : 'text-gray-400 hover:text-white'}`}
        >
            Home
        </button>
        <button 
            onClick={() => onNavigate('library')} 
            className={`transition-all duration-300 ${currentView === 'library' ? 'text-gold-400' : 'text-gray-400 hover:text-white'}`}
        >
            My Library
        </button>
        
        {user ? (
          <div className="flex items-center gap-4 pl-6 border-l border-white/10 relative">
             {appChildren !== undefined && onSelectChild && (
               <ChildSwitcher
                 children={appChildren}
                 activeChild={activeChild ?? null}
                 onSelect={onSelectChild}
                 onAddChild={() => onNavigate('profile-setup')}
               />
             )}
             <button
               onClick={onOpenPricing}
               className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border transition-all group ${
                 remaining === 0 
                    ? 'bg-red-500/10 border-red-500/30 text-red-300 hover:bg-red-500/20' 
                    : 'bg-white/5 border-white/10 text-gray-300 hover:bg-white/10 hover:border-gold-500/30 hover:text-white'
               }`}
             >
                <Sparkles className={`w-3.5 h-3.5 ${remaining === 0 ? 'text-red-400' : 'text-gold-400'}`} />
                <span className="text-xs font-bold">
                    {remaining}/{limit} Left
                </span>
             </button>

             <div className="relative">
                <button 
                    onClick={() => setShowDropdown(!showDropdown)}
                    className="flex items-center gap-3 text-white bg-gradient-to-r from-white/5 to-white/10 hover:from-gold-500/10 hover:to-forge-500/10 px-5 py-2.5 rounded-2xl border border-white/10 hover:border-gold-500/40 transition-all shadow-inner"
                >
                    <div className="w-6 h-6 rounded-full bg-gold-500/20 flex items-center justify-center">
                        <User className="w-3.5 h-3.5 text-gold-400" />
                    </div>
                    <span className="text-sm font-bold tracking-tight">{childName || user.name}</span>
                </button>

                {showDropdown && <DropdownMenu />}
             </div>
          </div>
        ) : (
          <div className="flex items-center gap-4 pl-6 border-l border-white/10">
            <button 
                onClick={() => onNavigate('login')} 
                className="text-gray-400 hover:text-white transition-colors"
            >
                Log In
            </button>
            <button 
              onClick={() => onNavigate('signup')}
              className="bg-gradient-to-r from-gold-500 to-forge-500 text-white px-7 py-2.5 rounded-2xl font-bold hover:brightness-110 transition-all shadow-lg shadow-forge-500/20 active:scale-95"
            >
              Sign Up
            </button>
          </div>
        )}
      </nav>
      
      {/* Mobile Action */}
      <div className="md:hidden flex gap-2 items-center">
         {user ? (
             <>
                 <button
                   onClick={onOpenPricing}
                   className={`flex items-center gap-1 px-2 py-1.5 rounded-lg border text-[9px] font-black uppercase tracking-tight ${
                     remaining === 0 
                        ? 'bg-red-500/10 border-red-500/30 text-red-300' 
                        : 'bg-white/5 border-white/10 text-gold-400'
                   }`}
                 >
                    {remaining}/{limit}
                 </button>
                 <button 
                    onClick={() => onNavigate('library')}
                    className="p-2 bg-white/5 rounded-xl border border-white/10 active:bg-white/10 transition-colors"
                 >
                    <Library className="w-5 h-5 text-gold-400" />
                 </button>
                 <div className="relative">
                    <button 
                        onClick={() => setShowDropdown(!showDropdown)}
                        className="p-2 bg-white/5 rounded-xl border border-white/10 active:bg-white/10 transition-colors"
                    >
                        <User className="w-5 h-5 text-gold-400" />
                    </button>
                    {showDropdown && <DropdownMenu />}
                 </div>
             </>
         ) : (
             <button 
                onClick={() => onNavigate('login')} 
                className="text-[10px] font-bold uppercase tracking-widest text-gold-400 border border-gold-500/30 px-3 py-1.5 rounded-lg"
             >
                Log In
             </button>
         )}
      </div>
    </header>
  );
};

export default Header;